import React, { useEffect } from 'react';
import {
  PrimaryButton,
  StepHeading,
  StepDescription,
  SecondaryButton,
} from '../styles';
import { PleriLogo } from '../../Pleri/components/PleriLogo';
import { AnimatedBackground } from '../components';
import { useOnboarding } from '../context/OnboardingContext';
import { UsagePage } from '../../usage/UsagePage';
import { ProtectedComponent } from '../../ProtectedComponent/ProtectedComponent';
import { useUserProfile } from '../../../api/profile';

import { useTranslation } from 'react-i18next';

const SecurityIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
    />
  </svg>
);

export const ChatIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
    />
  </svg>
);

const ChartIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z"
    />
  </svg>
);

interface LandingScreenProps {
  onContinue: () => void;
  onComplete?: () => void;
  setNestedScreen: (state: { isNested: boolean; onBack?: () => void }) => void;
}

export const LandingScreen: React.FC<LandingScreenProps> = ({
  onContinue,
  onComplete,
  setNestedScreen,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const { data: userProfile } = useUserProfile();
  const { setFooterContent } = useOnboarding();
  const [isUsageView, setIsUsageView] = React.useState(false);

  // Set footer content
  useEffect(() => {
    if (!isUsageView) {
      setFooterContent(
        <SecondaryButton
          onClick={onContinue}
          className="flex items-center gap-2"
        >
          <SecurityIcon className="h-5 w-5" />
          <span>{t('setUpToolsIntegrations')}</span>
        </SecondaryButton>,
      );
    } else {
      setFooterContent(null);
    }
  }, [onContinue, setFooterContent, isUsageView, setNestedScreen, t]);

  const handleChatWithMe = () => {
    // Close the onboarding flow
    onComplete?.();
  };

  const handleViewUsage = () => {
    setIsUsageView(true);
    const handleBack = () => {
      setIsUsageView(false);
      setNestedScreen({ isNested: false });
    };

    setNestedScreen({
      isNested: true,
      onBack: handleBack,
    });
  };

  if (isUsageView) {
    return <UsagePage />;
  }

  return (
    <div className="mx-auto">
      <AnimatedBackground>
        {/* Logo Section */}
        <div className="mb-8 text-center">
          <div className="relative mx-auto inline-block">
            <div className="absolute inset-0 animate-[pulse_4s_ease-in-out_infinite] rounded-xl bg-[#FF732E]/10 blur-xl" />
            <PleriLogo size={120} className="relative rounded-2xl" smallImage />
          </div>
        </div>

        {/* Heading Section */}
        <div className="mb-4 text-center">
          <StepHeading>
            {t('heyIReadyToHelpYou', { name: userProfile?.name })}
          </StepHeading>
        </div>

        <div className="mb-2 text-center">
          <StepDescription>{t('iSetUpMyBasicToolsAnd')}</StepDescription>
        </div>

        {/* Main Content */}
        <div className="mx-auto max-w-2xl px-4 md:px-8">
          {/* Tools Access Card */}
          <div className="flex items-start gap-4 rounded-xl bg-gradient-to-br from-[#FF732E]/5 to-[#E65A1F]/5 p-6">
            <div className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#FF732E] to-[#E65A1F] opacity-20" />
              <SecurityIcon className="relative h-7 w-7 text-[#FF732E]" />
            </div>
            <div>
              <h3 className="mb-1.5 text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('toolsIntegrations')}
              </h3>
              <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                {t('giveMeAccessToTheToolsYou')}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col items-center justify-center gap-4 md:flex-row">
          <PrimaryButton
            onClick={handleChatWithMe}
            className="flex w-full items-center justify-center gap-2 text-lg md:w-auto md:min-w-[200px]"
          >
            <ChatIcon className="h-5 w-5" />
            <span>{t('letGetToWork')}</span>
          </PrimaryButton>

          <ProtectedComponent permission="pleri:Write">
            <SecondaryButton
              onClick={handleViewUsage}
              className="flex w-full items-center justify-center gap-2 text-lg md:w-auto md:min-w-[200px]"
            >
              <ChartIcon className="h-5 w-5" />
              <span>{t('viewUsage')}</span>
            </SecondaryButton>
          </ProtectedComponent>
        </div>
      </AnimatedBackground>
    </div>
  );
};
